import { useState, useEffect, useCallback } from 'react';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  role: string;
  is_banned: boolean;
  is_superadmin: boolean;
  is_exempt: boolean;
  trial_ends_at: string | null;
  created_at: string;
  forms_count: number;
  subscription_status: string | null;
}

export interface AdminUserDetail {
  profile: AdminUser & { banned_reason: string | null; banned_at: string | null };
  forms_count: number;
  responses_count: number;
  subscription: Record<string, unknown> | null;
  recent_audit: Array<{ id: string; action: string; details: Record<string, unknown>; created_at: string }>;
}

interface UseAdminUsersParams {
  search?: string;
  status?: string | null;
  page?: number;
  perPage?: number;
}

interface UseAdminUsersReturn {
  users: AdminUser[];
  total: number;
  page: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAdminUsers({ search, status, page = 1, perPage = 25 }: UseAdminUsersParams): UseAdminUsersReturn {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      const { data, error: rpcError } = await supabase.rpc('admin_list_users', {
        p_search: search || null,
        p_status: status || null,
        p_page: page,
        p_per_page: perPage,
      });

      if (rpcError) {
        console.error('[useAdminUsers] RPC error:', rpcError);
        setError(rpcError.message);
      } else {
        setUsers(data.users);
        setTotal(data.total);
        setError(null);
      }
      setLoading(false);
    };
    fetchUsers();
  }, [search, status, page, perPage, refreshKey]);

  return { users, total, page, loading, error, refresh };
}

export async function fetchAdminUserDetail(userId: string): Promise<{ data: AdminUserDetail | null; error: string | null }> {
  const { data, error } = await supabase.rpc('admin_get_user_detail', { p_user_id: userId });
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}

export async function adminToggleBan(userId: string, banned: boolean, reason?: string): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('admin_toggle_user_ban', {
    p_user_id: userId,
    p_banned: banned,
    p_reason: reason || null,
  });
  return { error: error?.message || null };
}

export async function adminToggleExempt(userId: string, exempt: boolean): Promise<{ error: string | null }> {
  const { error } = await supabase.rpc('admin_toggle_exempt', {
    p_user_id: userId,
    p_exempt: exempt,
  });
  return { error: error?.message || null };
}

export async function adminCreateUser(
  email: string,
  password: string,
  fullName?: string,
  isExempt?: boolean
): Promise<{ data: { id: string; email: string } | null; error: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { data: null, error: 'Not authenticated' };

  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-create-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ email, password, full_name: fullName, is_exempt: isExempt }),
  });

  const result = await response.json();
  if (result.error) return { data: null, error: result.error };
  return { data: result.user, error: null };
}

export async function adminDeleteUser(
  userId: string,
  confirmEmail: string
): Promise<{ error: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { error: 'Not authenticated' };

  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-delete-user`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ user_id: userId, confirm_email: confirmEmail }),
  });

  const result = await response.json();
  return { error: result.error || null };
}

export async function adminUpdateUserProfile(
  userId: string,
  fields: { full_name?: string; email?: string; avatar_url?: string }
): Promise<{ error: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { error: 'Not authenticated' };

  const response = await fetch(`${SUPABASE_URL}/functions/v1/admin-update-user-profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ user_id: userId, ...fields }),
  });

  const result = await response.json();
  return { error: result.error || null };
}
